import * as React from 'react'
import produce from 'immer'
import { Form, Input, Spin } from 'antd'
import { set, get, cloneDeep } from 'lodash'
import { showError, resourceNotFound, ValidationError } from 'helpers/errors'
import { createLabelFactory, createFieldFactory, createHandleSubmit, isReadOnly } from 'helpers/formViews'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import Select, { Option } from 'elements/Select'
import FormButtons from 'elements/FormButtons'

const fileTypes = {
  csv: 'csv',
  excel: 'excel',
}

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
  }

  componentDidMount() {
    this.fetchItem()
  }

  transformSaveItemParams = ({ item, values }) => {
    const requiredFields = this.state.fileTemplateFields
      .filter((each) => each.mandatory)
      .map((each) => each.objectFieldName)

    return { fields: [...new Set([...requiredFields, ...item.fields])] }
  }

  getItemId = () =>
    [
      this.state.item?.id,
      this.props.linkTargetRecord?.fileTemplateId,
      this.props.linkTargetRecord?.templateId,
    ].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({ fileTemplateFileType: 'csv', fields: [] })
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
      ])

      const item = cloneDeep(responses[0].value.data)

      const response = item.objectType
        ? await this.props.getFileTemplateFields({ objectType: item.objectType })
        : null

      this.setState({
        item,
        fieldSettings: get(responses[1], 'value.data.fieldSettings', []),
        fileTemplateFields: get(response, 'value.data.items', []),
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        if (name === 'objectType') {
          set(draft.item, 'fields', [])
        }

        this.props.form.setFieldsValue({ [name]: value })
      }),
      async () => {
        if (name === 'objectType') {
          try {
            const response = await this.props.getFileTemplateFields({ objectType: value })

            this.setState({ fileTemplateFields: response.value.data.items })
          } catch (error) {
            showError({ error })
          }
        }
      }
    )

  render() {
    const { item, fieldSettings, fileTemplateFields } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const requiredFields = fileTemplateFields
      .filter((each) => each.mandatory)
      .map((each) => each.objectFieldName)
    const readOnly = isReadOnly(this)

    return (
      <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
        <div className="form-items-container">
          <ValidationError errors={this.state.validationErrors} />
          <Form.Item label={createLabel('name')}>
            {createFieldDecorator('name')(<Input readOnly={readOnly} autoFocus />)}
          </Form.Item>
          <Form.Item label={createLabel('objectType')}>
            {createFieldDecorator('objectType')(
              <Select
                onChange={(value) => this.setItemValue('objectType', value)}
                disabled={Boolean(item.id)}
                allowClear={false}
                readOnly={readOnly}
              >
                {Object.entries(this.props.allowedFileTemplateTypes).map(([key, value]) => (
                  <Option key={key} value={key}>
                    {t(value)}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
          <Form.Item label={createLabel('description')}>
            {createFieldDecorator('description')(<Input readOnly={readOnly} />)}
          </Form.Item>
          <Form.Item label={createLabel('fileTemplateFileType')}>
            {createFieldDecorator('fileTemplateFileType')(
              <Select allowClear={false} readOnly={readOnly}>
                {Object.entries(fileTypes).map(([key, value]) => (
                  <Option key={key} value={key}>
                    {t(value)}
                  </Option>
                ))}
              </Select>
            )}
          </Form.Item>
          <Form.Item label={t('requiredFields')}>
            <Select mode="multiple" placeholder={t('none')} value={requiredFields} disabled>
              {fileTemplateFields
                .filter((each) => each.mandatory)
                .map((each) => (
                  <Option key={each.id} value={each.objectFieldName}>
                    {t(each.displayName)}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item label={createLabel('fields')}>
            {createFieldDecorator('fields', {
              initialValue: item.fields.filter((each) => !requiredFields.includes(each)),
            })(
              <Select
                onChange={(value) => this.setItemValue('fields', value)}
                mode="multiple"
                allowClear={false}
              >
                {fileTemplateFields
                  .filter((each) => !each.mandatory)
                  .map((each) => (
                    <Option key={each.id} value={each.objectFieldName}>
                      {t(each.displayName)}
                    </Option>
                  ))}
              </Select>
            )}
          </Form.Item>
        </div>
        <div className="form-buttons-container">
          <FormButtons
            readOnly={readOnly}
            onCancel={this.props.onCancel}
            onSubmit={this.handleSubmit}
            saveButtonLoading={this.state.saveButtonLoading}
            saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
          />
        </div>
      </Form>
    )
  }
}

export default Form.create()(FormView)
